import React, { useState, useEffect, useMemo } from 'react';
import AxiosOICStat from '../AxiosOICStat';
import { Select, Button, Row, Col, Tooltip, Checkbox, Card, Collapse, Alert, message, Switch } from 'antd';
import DataTable from '../components/DataTable';
import IndicatorSelector from '../components/IndicatorSelector';
import { useSelector } from 'react-redux';
import DatePicker from 'react-multi-date-picker';
import DatePanel from "react-multi-date-picker/plugins/date_panel"
import PresetValuesPlugin from '../components/datePickerPlugins/PresetValuesPlugin';
import Icon from "react-multi-date-picker/components/icon";
import { CarryForwardCheck } from 'components/CarryForwardCheck';
import CategoriesCollapse from 'components/CategoriesCollapse';
import * as XLSX from 'xlsx';
import { writeExcelFile } from '../utils/excelExport';
import { getIndicatorName } from '../utils/indicatorName';
import AggregationExportService from 'components/exportServices/AggregationExportService';

const { Option } = Select;

export function AggregationQuery({ auto_search = false }) {
    const [selectedIndicators, setSelectedIndicators] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedYears, setSelectedYears] = useState([]);
    const [yearsFormat, setYearsFormat] = useState("Ascending")
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const indicatorsState = useSelector((state) => state.indicators);
    const unitsState = useSelector((state) => state.units);
    const countriesState = useSelector((state) => state.countries);
    const [regionMappingsData, setRegionMappingsData] = useState([]);
    const [countryMap, setCountryMap] = useState({});
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [aggregationIndicators, setAggregationIndicators] = useState([]);
    const [mopDataWarn, setMopDataWarn] = useState(false);
    const [yearsRange, setYearsRange] = useState([]);
    const [carryForwardCheck, setCarryForwardCheck] = useState(false);
    const [carryForwardBaseYear, setCarryForwardBaseYear] = useState('')
    const categoriesState = useSelector((state) => state.categories);
    const [keyIndicators, setKeyIndicators] = useState([]);
    const [isKeyIndicatorsFetching, setIsKeyIndicatorsFetching] = useState(false);
    const [fetchLatest, setFetchLatest] = useState(false);
    const [columns, setColumns] = useState([]);
    const [isColYearView, setIsColYearView] = useState(true);
    const [exportedRawData, setExportedRawData] = useState([]);
    const [aggrExportService, setAggrExportService] = useState(null);

    const renderTagPlaceholder = (omittedValues) => {
        // Sort the omitted values alphabetically based on their labels
        const sortedOmittedValues = [...omittedValues].sort((a, b) => parseInt(a.value) - parseInt(b.value));

        // Create a multiline string of all sorted omitted values
        const fullList = sortedOmittedValues.map(val => val.label).join('\n');
        return (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{fullList}</span>}>
                <span>{`+${omittedValues.length} more`}</span>
            </Tooltip>
        );
    };

    useEffect(() => {
        if (indicatorsState.status === 'succeeded') {
            AxiosOICStat.get('/indicators/descriptions').then(response => {
                let temp = [];
                response.data.forEach((ind) => {
                temp.push(ind.ind_code + " - " + getIndicatorName(ind));
                });
                setAggregationIndicators(temp);
            }
            );
        }
    }, [indicatorsState.status, indicatorsState.value]);


    useEffect(() => {
        // Assume these are the endpoints to fetch platform and mission data
        if (!auto_search) {

        }
        else {
            handleSearch();
        }
    }, [auto_search]);

    useEffect(() => {
        if(!isKeyIndicatorsFetching) {
            AxiosOICStat.post('/secondary-categories/get-key-indicators').then(res => {
                setKeyIndicators(res.data);
            })
            setIsKeyIndicatorsFetching(true);
        } 
        //console.log("Key Indicators",keyIndicators);
    }, [keyIndicators]);

    const handleCalculate = () => {
        setLoading(true);
        setMopDataWarn(false);
        const formattedYears = [];

        const missing = [];
        if (!fetchLatest && selectedYears.length === 0) missing.push("year");
        if (selectedIndicators.length === 0) missing.push("indicator");
        if (selectedRegions.length === 0) missing.push("region");
            
        if (missing.length > 0) {
            message.warning(`Please select at least one ${missing.join(", ")}.`);
            setLoading(false);
            return;
        }

        selectedYears.forEach(range => {
            if (range.length === 2) {
                for (let i = range[0].year; i <= range[1].year; i++) {
                    formattedYears.push(i);
                }
            } else {
                formattedYears.push(range[0].year);
            }
        });

        setYearsRange(formattedYears);
        const formattedIndicators = selectedIndicators.map(indicator => indicator.split(' - ')[0]);
        const intListForFormattedIndicators = formattedIndicators.map(ind => parseInt(ind)); 

        if(fetchLatest){
            AxiosOICStat.post('/aggregations/get-latest-data', {
                regions: selectedRegions,
                indicators: intListForFormattedIndicators,
                first_year_range: [],
                last_year_range: []
            }).then(response => {
                setExportedRawData(response.data);
                const updatedData = response.data.map((element, index) => {
                    element.key = index;
                    element.u_name = unitsState.value[element.u_code]?.unit_name_eng;
                        element.countries_used = element.d_value_dict?.countries_used || element.d_value_last_dict?.countries_used || element.d_value_first_dict?.countries_used || [];
                    element.d_value = element.d_value_last === null ? ".." : Number(element.d_value_last);
                    element.c_name = element.c_name || element.r_name || "";
                    element.c_name = element.c_name || element.r_name || "";
                    return element;
                });
                updatedData.forEach(element => {
                    element.d_value = Number(element.d_value);
                    element.r_name = regionMappingsData.find(region => region.id === element.r_code)?.region_name;
                    element.d_year = element.d_year_last;
                    element.d_last_update_date = indicatorsState.value[element.ind_code]?.d_last_update_date || null;
                    element.weighted_ind = getIndicatorName(indicatorsState.value[element.weighted_ind_code]) || null;

                    if(element.d_date == null || element.d_date == undefined){
                        element.d_date = new Date(Date.now()).toLocaleDateString();
                    }
                });
                console.log("Updated Data", updatedData);
                setSearchResults(updatedData);
                setColumns(["r_name", "c_name", "ind_name", "d_date", "u_name", "weighted_ind", "countries_used", "d_year", "d_value"]);
                setIsColYearView(false);
                setLoading(false);
            });
            return;
        }
        
        if(carryForwardCheck){
            setMopDataWarn(true)
            AxiosOICStat.post('/aggregations/calculate-carry-forward', {
                regions: selectedRegions,
                indicators: formattedIndicators,
                years: formattedYears,
                base_year: carryForwardBaseYear
            }).then(response => {
                const updatedData = response.data.map((element, index) => {
                    element.key = index;
                    element.u_name = unitsState.value[element.u_code]?.unit_name_eng;
                        element.countries_used = element.d_value_dict?.countries_used || element.d_value_last_dict?.countries_used || element.d_value_first_dict?.countries_used || [];
                    return element;
                });
                updatedData.forEach(element => {
                    element.r_name = regionMappingsData.find(region => region.id === element.r_code)?.region_name;
                });
                //customSetSearchResults(updatedData);
                setLoading(false);
                
                const transformedData = [];
    
                updatedData.forEach((item) => {
                    //console.log("ITEM:", item)
                    let existingEntry = transformedData.find(
                        (entry) => entry.ind_code === item.ind_code && entry.r_code === item.r_code
                    );
                    
                    const valuesArray = Object.values(indicatorsState.value); //Converting Array
                    const ind = valuesArray.find((entry) => entry.ind_code === item.ind_code);
                    const ind_name = getIndicatorName(ind)
                    if (!existingEntry) {
                        existingEntry = {
                            key: transformedData.length,
                            r_code: item.r_code,
                            ind_name: ind_name,
                            ind_code: item.ind_code,
                            r_name: item.r_name,
                            u_name: item.u_name,
                            u_code: item.u_code,
                            agg_type: item.agg_type,
                            formatted_d_value: item.d_value,
                            d_date: item.d_date,
                            c_code: null,
                            c_name: item.c_name || item.r_name || null,
                            cat_name: null,
                            countries_used: item.d_value_dict?.countries_used || [],
                            weighted_ind: getIndicatorName(indicatorsState.value[item.weighted_ind_code])
                        };
                        transformedData.push(existingEntry);
                    }
        
                    existingEntry[item.d_year] = item.d_value;
                });
                //setSearchResults(updatedData);
                setSearchResults(transformedData);
                setIsColYearView(true);
                setColumns(["r_name", "c_name", "ind_name", "d_date", "u_name", "weighted_ind", "countries_used"]);
            })
        }
        else{
            AxiosOICStat.post('/aggregations/calculate', {
                regions: selectedRegions,
                indicators: formattedIndicators,
                years: formattedYears
            }).then(response => {
                setExportedRawData(response.data);
                const updatedData = response.data.map((element, index) => {
                    element.key = index;
                    element.u_name = unitsState.value[element.u_code]?.unit_name_eng;
                    element.d_value = element.d_value === null ? ".." : Number(element.d_value); // Replace NaN with "N/A"
                    element.c_name = element.c_name || element.r_name || "";
                    return element;
                });
                updatedData.forEach(element => {
                    element.d_value = Number(element.d_value);
                    element.r_name = regionMappingsData.find(region => region.id === element.r_code)?.region_name;
                    element.countries_used = element.countries_used || [];
                });
                //console.log("Updated Data", updatedData);
                //customSetSearchResults(updatedData);
                setLoading(false);
                
                const transformedData = [];
    
                updatedData.forEach((item) => {
                    //console.log("ITEM:", item)
                    let existingEntry = transformedData.find(
                        (entry) => entry.ind_code === item.ind_code && entry.r_code === item.r_code
                    );
                    
                    const valuesArray = Object.values(indicatorsState.value); //Converting Array
                    const ind = valuesArray.find((entry) => entry.ind_code === item.ind_code);
                    const ind_name = getIndicatorName(ind)
                    if (!existingEntry) {
                        existingEntry = {
                            key: transformedData.length,
                            r_code: item.r_code,
                            ind_name: ind_name,
                            ind_code: item.ind_code,
                            r_name: item.r_name,
                            u_name: item.u_name,
                            u_code: item.u_code,
                            agg_type: item.agg_type,
                            formatted_d_value: item.d_value,
                            d_date: item.d_date,
                            c_code: null,
                            c_name: item.c_name || item.r_name || null,
                            cat_name: null,
                            countries_used: item.d_value_dict?.countries_used || [],
                            weighted_ind: getIndicatorName(indicatorsState.value[item.weighted_ind_code])
                        };
                        transformedData.push(existingEntry);
                    }
        
                    existingEntry[item.d_year] = item.d_value;
                });
    
                //console.log(transformedData);
                //console.log(updatedData);
                //setSearchResults(updatedData);
                setSearchResults(transformedData);
                setColumns(["r_name", "c_name", "ind_name", "d_date", "u_name", "weighted_ind", "countries_used"]);
                setIsColYearView(true);
                //console.log("Transformed Data", transformedData);
    
            });
        }       
    }

    const handleSearch = () => {
        setLoading(true);
        setMopDataWarn(false);
        const formattedYears = [];

        selectedYears.forEach(range => {
            if (range.length === 2) {
                for (let i = range[0].year; i <= range[1].year; i++) {
                    formattedYears.push(i);
                }
            } else {
                formattedYears.push(range[0].year);
            }
        });

        const formattedIndicators = selectedIndicators.map(indicator => indicator.split(' - ')[0]);

        AxiosOICStat.post('/aggregations/warning', {
            regions: selectedRegions,
            indicators: formattedIndicators,
            years: formattedYears
        }).then(response => {
            if(response.data){
                message.warning("There is new calculation. Please wait...");
            }
        });

        AxiosOICStat.post('/aggregations/query', {
            regions: selectedRegions,
            indicators: formattedIndicators,
            years: formattedYears
        }).then(response => {
            const updatedData = response.data.map((element, index) => {
                element.key = index;
                element.u_name = unitsState.value[element.u_code]?.unit_name_eng;
                element.countries_used = element.d_value_dict?.countries_used || element.d_value_last_dict?.countries_used || element.d_value_first_dict?.countries_used || [];
                return element;
            });
            updatedData.forEach(element => {
                element.r_name = regionMappingsData.find(region => region.id === element.r_code)?.region_name;
            });
            

            //console.log("Updated Data", lastUpdatedData);
            setSearchResults(updatedData);  
            setLoading(false);
        });

    };

    const handleCarryForward = (checked) => {
        setCarryForwardCheck(checked);
    };

    const handleYearChange = (year) => {
        setCarryForwardBaseYear(year);
    }

    //const customSetSearchResults = (updatedData) => {
    //    for (let index = 0; index < updatedData.length; index++) {
    //        const item = updatedData[index];
    //        if (item.d_value == 0 || item.d_value == null){
    //            if(index > 0){
    //                const prevItem = updatedData[index-1]
    //                if(prevItem.d_value != 0 && prevItem.d_value != null){
    //                    item.d_value = prevItem.d_value;
    //                    item.agg_type = item.agg_type+"*"
    //                    setMopDataWarn(true);
    //                }
    //            }
    //        }
    //    }
    //    console.log(updatedData)
    //    setSearchResults(updatedData);
    //}

    const modifyCarryForwardValues = (data) => {
        let lastValue = null;
    
        // Loop through the data and modify values if they're the same as the previous year
        const modifiedData = data.map((entry) => {
            const newEntry = { ...entry };
            var tempKeyArray = Object.keys(newEntry)
            //console.log(tempKeyArray)

            for (let index = 1; index < tempKeyArray.length; index++) {
                const yearKey = tempKeyArray[index];
                const prevYearKey = tempKeyArray[index - 1];

                //return
                if(yearKey == "key"){break;}
                else{
                    if(newEntry[yearKey] == newEntry[prevYearKey]){
                        let value = newEntry[yearKey]
                        newEntry[yearKey] = `CF: ${value}`
                    }
                }

            }

            return newEntry
        });
        //console.log(modifiedData)
        return modifiedData;
    };

    const exportCarryForwardExcel = () => {
        // Get the modified data
        var modifiedData = modifyCarryForwardValues(searchResults);

        const fileName = "Carry-Forward Results";

        // Keys to drop
        const keysToDrop = ['c_code', 'cat_name', 'formatted_d_value', 'd_date', 'u_code', 'r_code', 'ind_code', 'c_name', 'key'];

        // Rename columns
        const renameColumns = {
            "agg_type": "Aggregation Type",
            "ind_name": "Indicator Name",
            "r_name": "Region Name",
            "u_name": "Unit Name"
        };

        // Extract year keys dynamically
        const yearKeys = Object.keys(modifiedData[0]).filter(key => /^\d{4}$/.test(key)); // Regex to match year format (e.g., '2019', '2020')

        // Define the desired order of columns dynamically
        const desiredOrder = [
            "Region Name",
            "Indicator Name",
            "Unit Name",
            "Aggregation Type",
            ...yearKeys // Add the dynamic year keys
        ];
        let filteredData;

        if (Array.isArray(modifiedData)) {
            filteredData = modifiedData.map(item => 
                Object.keys(item)
                    .filter(key => !keysToDrop.includes(key))
                    .reduce((obj, key) => {
                        obj[renameColumns[key] || key] = item[key];
                        return obj;
                    }, {})
            );
        } else {
            filteredData = Object.keys(modifiedData)
                .filter(key => !keysToDrop.includes(key))
                .reduce((obj, key) => {
                    obj[renameColumns[key] || key] = modifiedData[key]; 
                    return obj;
                }, {});

            filteredData = [filteredData];
        }

        const containsNumber = (str) => {
            return /\d/.test(str);
        };

        filteredData = filteredData.map(item => {
            const reorderedItem = {};
            desiredOrder.forEach(key => {
                if (containsNumber(key)){
                    reorderedItem[`Year: ${key}`] = item[key] !== undefined ? item[key] : null;
                }
                else{
                    reorderedItem[key] = item[key] !== undefined ? item[key] : null;
                }
                //console.log(reorderedItem)
            });
            
            return reorderedItem;
        });

        const worksheet = XLSX.utils.json_to_sheet(filteredData);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        writeExcelFile(workbook, `${fileName}.xlsx`);
    };

    useEffect(() => {
        if (exportedRawData && exportedRawData.length > 0) {
            const dValueDictList = exportedRawData.map(item => {
                if (item.d_value_dict) return item.d_value_dict;
                if (item.d_value_last_dict) return item.d_value_last_dict;
                if (item.d_value_first_dict) return item.d_value_first_dict;
                return null;
            }).filter(dict => dict !== null && dict !== undefined);
            const exportService = AggregationExportService(dValueDictList, countriesState, indicatorsState, unitsState, regionMappingsData, categoriesState);
            console.log("Aggregation Export Service initialized:", exportService);
            setAggrExportService(exportService);
        } else {
            setAggrExportService(null);
        }
    }, [exportedRawData, countriesState, indicatorsState, unitsState, regionMappingsData, categoriesState]);

    const exportDetails = () => {
        if (aggrExportService) {
            aggrExportService.exportDetailsToExcel();
        } else {
            message.warning('No data available for export');
        }
    };

    const exportToExcel = () => {
        if (fetchLatest) {
            const headers = [
                "Region Name", "Indicator Code", "Indicator", "Unit", "Last Update Date", "Weighted Indicator", "Year", "Value"
            ];

            // Map searchResults to the desired format
            const dataForExport = searchResults.map(item => ({
                "Region Name": regionMappingsData.find(region => region.id === item.r_code)?.region_name || "..",
                "Indicator Code": item.ind_code || "..",
                "Indicator": getIndicatorName(indicatorsState.value[item.ind_code]) || item.ind_name || "..",
                "Unit": unitsState.value[item.u_code]?.unit_name_eng || item.u_name || "..",
                "Last Update Date": item.d_last_update_date || item.d_date || "..",
                "Weighted Indicator": getIndicatorName(indicatorsState.value[item.weighted_ind_code]) || item.weighted_ind || "..",
                "Year": item.d_year_last || item.d_year || "..",
                "Value": item.d_value_last || item.d_value || ".."
            }));

            // Prepare worksheet data with headers
            const worksheetData = [headers, ...dataForExport.map(row => headers.map(header => row[header]))];

            // Create a new workbook and worksheet
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'OICStat Data');

            // Export the workbook to an Excel file
            const fileName = selectedIndicators.length === 1 ? `Latest Aggregation - ${selectedIndicators[0].split(' - ')[1]}` : 'Latest Aggregation';
            writeExcelFile(workbook, `${fileName}.xlsx`);
            return;
        }
        const yearColumns = [];
        console.log("Search Results:", searchResults);
        searchResults.forEach(item => {
            Object.keys(item).forEach(key => {
                if (!isNaN(key) && !yearColumns.includes(key)) {
                    yearColumns.push(key);
                }
            });
        });
        yearColumns.sort(); // Sort the years in ascending order

        // Define the fixed headers
        const headers = [
            "Region Name", "Indicator Code", "Indicator", "Unit", "Last Update Date", "Weighted Indicator", ...yearColumns
        ];

        // Map `searchResults` to the format suitable for Excel export
        console.log(searchResults);
        const dataForExport = searchResults.map(item => {
            const row = {
                "Region Name": regionMappingsData.find(region => region.id === item.r_code)?.region_name || "..",
                "Indicator Code"  : item.ind_code || "..",
                "Indicator": getIndicatorName(indicatorsState.value[item.ind_code]) || "..",
                "Unit": unitsState.value[item.u_code]?.unit_name_eng || "..",
                "Last Update Date": item.d_date || "..",
                "Weighted Indicator": item.weighted_ind || "..",
            };

            // Add dynamic year values with formatted numbers (up to 3 decimal places)
            yearColumns.forEach(year => {
                const value = Number(item[year]);
                if (typeof value === 'number') {
                    row[year] = Number(value.toFixed(3)); // Format to 3 decimal places
                } else {
                    row[year] = value || "..";
                }
            });

            return row;
        });

        // Prepare worksheet data with headers
        const worksheetData = [headers, ...dataForExport.map(row => headers.map(header => row[header]))];

        // Create a new workbook and worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'OICStat Data');

        // Export the workbook to an Excel file
        const fileName = selectedIndicators.length == 1 ? `Aggregation - ${selectedIndicators[0].split(' - ')[1]}` : 'Aggregation';
        console.log("Exporting to Excel with file name:", fileName);
        writeExcelFile(workbook, `${fileName}.xlsx`);

        //console.log(searchResults);
    };

    useEffect(() => {
        // sort the selected values
        setSelectedIndicators(selectedIndicators.sort((a, b) => parseInt(a) - parseInt(b)));
        setSelectedYears(selectedYears.sort((a, b) => parseInt(a) - parseInt(b)));
    },
        [selectedIndicators, selectedYears]
    );

    useEffect(() => {
        // Fetch the region mappings when the component is mounted
        const fetchRegionMappings = async () => {
            try {
                const response = await AxiosOICStat.get('/region-mappings/'); // Adjust the endpoint as needed
                setRegionMappingsData(response.data); // Assuming the response body will be the array of region mappings
            } catch (error) {
                console.error('Failed to fetch region mappings:', error);
                // Handle errors, e.g., show notification
            }
        };

        fetchRegionMappings();
    }, []);

    const regions = [
        {
            id: 1,
            label: 'Regions',
            children: <Row gutter={[8, 8]}>
                {regionMappingsData && regionMappingsData.map((region) => (
                    <Col span={6} key={region.id}>
                        <Checkbox
                            key={region.id}
                            checked={selectedRegions.includes(region.id)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedRegions([...selectedRegions, region.id]);
                                } else {
                                    setSelectedRegions(selectedRegions.filter((id) => id !== region.id));
                                }
                            }}
                            value={region.id}
                        >
                            {region.region_name}
                        </Checkbox>
                    </Col>
                ))}
            </Row>
        }
    ]


    const handleCategoryChange = (categoryId, isChecked) => {
        const indicatorsInCategory = Object.values(indicatorsState.value || {}).filter(
            (indicator) => indicator.cat_code === categoryId || indicator.category_codes.includes(categoryId)
        ).map((indicator) => `${indicator.ind_code} - ${getIndicatorName(indicator)}`); // Match Select items' format
    
        if (isChecked) {
            // Add category and its indicators
            if(categoryId === 32){
                const formattedKeyIndicators = keyIndicators.map(
                    (indicator) => `${indicator.ind_code} - ${getIndicatorName(indicator)}`
                );
                setSelectedCategories((prev) => [...prev, categoryId]);
                setSelectedIndicators((prev) => [...new Set([...prev, ...formattedKeyIndicators])]);
            }
            else if(categoryId === 27){
                setSelectedCategories((prev) => [...prev, categoryId]);
            }
            else{
                setSelectedCategories((prev) => [...prev, categoryId]);
                setSelectedIndicators((prev) => [...new Set([...prev, ...indicatorsInCategory])]);
            }
        } else {

            if(categoryId === 32){
                const formattedKeyIndicators = keyIndicators.map(
                    (indicator) => `${indicator.ind_code} - ${getIndicatorName(indicator)}`
                );
                setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
                setSelectedIndicators((prev) =>
                    prev.filter((indicator) => !formattedKeyIndicators.includes(indicator))
                );
            }
            // Remove category and its indicators
            setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
            setSelectedIndicators((prev) =>
                prev.filter((indicator) => !indicatorsInCategory.includes(indicator))
            );
        }
    };

    const handleGoalSelected = (categoryId, goals) => {
        const currentIndicators = selectedIndicators;
        const goalsArray = Array.isArray(goals) ? goals : (goals ? [goals.toString()] : []);
        
        const allSdgPatterns = Array.from({length: 17}, (_, i) => new RegExp(`^SDG\\s*0*${i+1}\\.`, 'i'));
        const allSdgIndicators = Object.values(indicatorsState.value || {}).filter(
            (indicator) => allSdgPatterns.some(pattern => pattern.test(indicator.ind_name_eng))
        ).map((indicator) => `${indicator.ind_code} - ${getIndicatorName(indicator)}`);

        const selectedPatterns = goalsArray.map(goal => new RegExp(`^SDG\\s*0*${goal}\\.`, 'i'));
        const selectedSdgIndicators = Object.values(indicatorsState.value || {}).filter(
            (indicator) => selectedPatterns.some(pattern => pattern.test(indicator.ind_name_eng))
        ).map((indicator) => `${indicator.ind_code} - ${getIndicatorName(indicator)}`);

        const filteredIndicators = currentIndicators.filter(ind => !allSdgIndicators.includes(ind));
        setSelectedIndicators([...new Set([...filteredIndicators, ...selectedSdgIndicators])]);
    };
    


    return (
        <>
            {!auto_search &&
                <Col style={{ marginBottom: '10px' }}>
                    <Collapse size="small" items={regions} bordered={false} style={{ marginBottom: "10px", width: "100%", backgroundColor: "white" }} />
                    <CategoriesCollapse selectedCategories={selectedCategories} onChange={handleCategoryChange} onGoalSelected={handleGoalSelected} />
                    <br />
                    <Row style={{marginBottom: '10px'}}>
                        <CarryForwardCheck carryForwardCheck={carryForwardCheck} onYearChange={handleYearChange} onSwitchChange={handleCarryForward} isDisabled={fetchLatest}></CarryForwardCheck>
                    </Row>
                    <Row style={{ marginBottom: '10px' }}>
                        <Switch
                            checked={fetchLatest}
                            onChange={setFetchLatest}
                            checkedChildren="Last Available Data"
                            unCheckedChildren="Last Available Data"
                            style={{ marginRight: 10, width: 'fit-content' }}
                        />
                    </Row>
                    <Row>
                        <IndicatorSelector
                          indicators={Object.values(indicatorsState.value || {})}
                          selectedIndicators={selectedIndicators}
                          onChange={setSelectedIndicators}
                          minWidth="500px"
                        />

                        <Button disabled={fetchLatest} shape="circle" value="large" type="primary" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <DatePicker
                                onlyYearPicker
                                multiple
                                range
                                sort
                                highlightToday={false}
                                placeholderText={"Please select a date"}
                                selected={selectedYears}
                                value={selectedYears}
                                onChange={setSelectedYears}
                                maxDate={new Date()}
                                minDate={new Date(1970, 0, 1)}
                                plugins={[
                                    <DatePanel />,
                                    <PresetValuesPlugin position="left" setValue={setSelectedYears} />,
                                ]}
                                render={<Icon style={{ color: 'white', width: '85%', marginTop: '2px' }} />}
                            />
                        </Button>
                        <Button type="primary" onClick={handleCalculate} style={{ marginLeft: '10px', }}>Calculate</Button>
                    </Row>
                </Col>
            }
            <Row>
                <Col xs={24}>
                 <Select
                 placeholder="Years Order (Default:Ascending)"
                 onChange={setYearsFormat}
                style={{ minWidth: '500px', marginBottom: '10px', marginRight: '10px', maxWidth: '1000px' }}
                 >
                    <Option key={"ascending"} value={"Ascending"}>Ascending</Option>
                    <Option key={"descending"} value={"Descending"}>Descending</Option>
                 </Select>   
                </Col>
            </Row>
            <Row>
                {mopDataWarn && <Alert style={{ marginBottom: "10px" }} type="warning" message={<span><strong>Warning:</strong> There is carry-forward data.</span>} />}
                <Col xs={24}>
                    <DataTable className={"custom-table"} data={searchResults} columns={columns} widths={['15%', '55%', '10%', '10%']} loading={loading}
                            colYearView={isColYearView} yearRange={yearsRange} yearOrder={yearsFormat} showIndexColumn={false} decimalSymbol={"."}/>
                </Col>
            </Row>

            <Button onClick={exportToExcel} type="primary" style={{ marginLeft: '10px' }}>
                Export to CSV
            </Button>
            <Button onClick={exportDetails} type="primary" style={{ marginLeft: '10px' }}>
                Export Details
            </Button>
        </>
    );
};